// Acceso del chofer (P0, decisión D51): como todos, con el correo y la contraseña o con Google, Apple o Facebook si el
// servidor los tiene. Con el celular y un código (o el código temporal del encargado, D16) solo si la empresa lo prendió
// (`acceso.celular`). Cualquiera sea la forma, la sesión y lo que queda en el celular son los mismos.
import type { Proveedor } from '@reparto/shared/nucleo';
import { api } from './api';
import { db, type Sesion } from './db';
import type { Usuario } from './tipos';

interface RespuestaLogin {
  access_token: string;
  refresh_token: string;
  dispositivo_id: string;
  usuario: Usuario;
}

const dispositivo = () => ({
  nombre: (typeof navigator !== 'undefined' ? navigator.userAgent : 'web').slice(0, 70),
  plataforma: 'web' as const,
});

/** El correo con que se entró la última vez en este celular: la pantalla de acceso lo trae escrito. */
const CLAVE_CORREO = 'reparto.chofer.correo';
export function ultimoCorreo(): string {
  try {
    return localStorage.getItem(CLAVE_CORREO) ?? '';
  } catch {
    return '';
  }
}
function recordarCorreo(email: string | null): void {
  try {
    if (email) localStorage.setItem(CLAVE_CORREO, email);
  } catch {
    /* sin almacenamiento: se escribe de nuevo */
  }
}

export function solicitarCodigo(telefono: string) {
  return api<{ enviado: true; expira_en: string; codigo_desarrollo?: string }>('/auth/otp/solicitar', {
    cuerpo: { telefono },
    sinToken: true,
  });
}

async function guardar(r: RespuestaLogin): Promise<Sesion> {
  const previa = await db.sesion.get('actual');
  const s: Sesion = {
    id: 'actual',
    access_token: r.access_token,
    refresh_token: r.refresh_token,
    dispositivo_id: r.dispositivo_id,
    usuario: r.usuario,
    empresa: { id: r.usuario.empresa_id, nombre: '' },
    // Si vuelve a entrar el mismo usuario, conserva el móvil elegido y su trabajo pendiente.
    movil_id: previa?.usuario.id === r.usuario.id ? previa.movil_id : null,
    fecha: previa?.usuario.id === r.usuario.id ? previa.fecha : null,
  };
  if (previa && previa.usuario.id !== r.usuario.id) await borrarDatosLocales();
  await db.sesion.put(s);
  recordarCorreo(r.usuario.email);
  try {
    const yo = await api<{ empresa: { id: string; nombre: string } }>('/auth/yo');
    await db.sesion.update('actual', { empresa: yo.empresa });
  } catch {
    /* sin datos de empresa no pasa nada */
  }
  return s;
}

/** Con el correo y la contraseña (`app: chofer`: si el rol no entra a la app, la API dice por qué). */
export async function entrarConClave(email: string, password: string) {
  return guardar(
    await api<RespuestaLogin>('/auth/login', {
      cuerpo: { email: email.trim().toLowerCase(), password, app: 'chofer', dispositivo: dispositivo() },
      sinToken: true,
    }),
  );
}

export async function entrarConOtp(telefono: string, codigo: string) {
  return guardar(
    await api<RespuestaLogin>('/auth/otp/verificar', {
      cuerpo: { telefono, codigo, dispositivo: dispositivo() },
      sinToken: true,
    }),
  );
}

export async function entrarConCodigoAcceso(codigo: string) {
  return guardar(
    await api<RespuestaLogin>('/auth/codigo', { cuerpo: { codigo, dispositivo: dispositivo() }, sinToken: true }),
  );
}

/**
 * Con Google, Apple o Facebook (decisiones D50 y D51): canjea el código de un uso de la vuelta con el secreto de la
 * ida. La sesión y lo que queda en el celular son los mismos que con la contraseña: si entra otro usuario, se borra lo
 * del anterior.
 */
export async function entrarConProveedor(proveedor: Proveedor, codigo: string, secreto: string) {
  return guardar(
    await api<RespuestaLogin>(`/auth/${proveedor}/canjear`, {
      cuerpo: { app: 'chofer', codigo, secreto, dispositivo: dispositivo() },
      sinToken: true,
    }),
  );
}

/** Operaciones y mensajes del chat que todavía no llegaron al servidor. */
export async function pendientes(): Promise<number> {
  const [ops, chat] = await Promise.all([
    db.cola.where('estado').anyOf('pendiente', 'enviando', 'rechazada').count(),
    db.chatPendientes.count(),
  ]);
  return ops + chat;
}

export async function salir(): Promise<void> {
  try {
    await api('/auth/salir', { metodo: 'POST' });
  } catch {
    /* sin red: igual se cierra localmente */
  }
  if ((await pendientes()) > 0) {
    // Se conserva el trabajo: el próximo ingreso del mismo usuario lo envía.
    await db.sesion.update('actual', { access_token: '', refresh_token: '' });
    return;
  }
  await borrarDatosLocales();
}

export async function borrarDatosLocales(): Promise<void> {
  const tablas = [
    ...[db.sesion, db.paquete, db.moviles, db.cola, db.archivos, db.posiciones, db.escaneos, db.cargas],
    ...[db.chatConversaciones, db.chatMensajes, db.chatPendientes, db.chatArchivos, db.chatMarcas],
  ];
  await db.transaction('rw', tablas, async () => {
    await Promise.all(tablas.map((t) => t.clear()));
  });
  try {
    localStorage.removeItem(CLAVE_CORREO);
  } catch {
    /* nada que borrar */
  }
}
